import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';

import { PAGE_SIZE_SM } from '~constants/common';

import { formatCompactCurrency } from '~libs/formatters';

import { BreakdownItem } from '~components/visualisation/statistic/Statistic';

import { useGetStatementBudgetsSummaryQuery } from '~support-at-home/actions/statementBudgetsSummary';

interface BreakdownSet {
  opening: BreakdownItem[];
  spent: BreakdownItem[];
  closing: BreakdownItem[];
}

export const useStatementBudgetSummary = (isActive: boolean) => {
  const [statementMonth, setStatementMonth] = useState<string>(() => dayjs().format('YYYY-MM'));
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);

  const handleMonthChange = useCallback((_id: string, value: string | null) => {
    setStatementMonth(value ?? '');
    setCurrentPage(1);
    setVisibleColumns([]);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const queryArgs = useMemo(
    () => ({
      statement_month: statementMonth,
      limit: PAGE_SIZE_SM,
      offset: (currentPage - 1) * PAGE_SIZE_SM,
    }),
    [currentPage, statementMonth],
  );

  const { data, isLoading, isFetching, isError, error } = useGetStatementBudgetsSummaryQuery(queryArgs, {
    skip: !isActive || !statementMonth,
  });

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE_SM) : 0;

  // Fix #1: Wrap in useMemo to maintain stable reference when data is undefined
  const fundingTypes = useMemo(() => data?.summary?.funding_types ?? [], [data?.summary?.funding_types]);

  const allFundingTypeLabels = useMemo(() => fundingTypes.map((ft) => ft.budget_type_label), [fundingTypes]);

  const columnFilterItems = useMemo(
    () => fundingTypes.map((ft) => ({ key: ft.budget_type_label, label: ft.budget_type_label })),
    [fundingTypes],
  );

  // Fix #2: Include visibleColumns in dependency array
  useEffect(() => {
    if (allFundingTypeLabels.length > 0 && visibleColumns.length === 0) {
      setVisibleColumns(allFundingTypeLabels);
    }
  }, [allFundingTypeLabels, visibleColumns]);

  const visibleLabels = useMemo(() => (visibleColumns.length > 0 ? visibleColumns : null), [visibleColumns]);

  // Fix #6: Combine 3 breakdown useMemos into 1
  const breakdowns: BreakdownSet = useMemo(() => {
    const opening: BreakdownItem[] = [];
    const spent: BreakdownItem[] = [];
    const closing: BreakdownItem[] = [];
    for (const ft of fundingTypes) {
      const label = ft.budget_type_label;
      opening.push({ label, value: formatCompactCurrency(ft.total_opening_balance) });
      spent.push({ label, value: formatCompactCurrency(ft.total_spent) });
      closing.push({ label, value: formatCompactCurrency(ft.total_closing_balance) });
    }
    return { opening, spent, closing };
  }, [fundingTypes]);

  const summary = data?.summary;
  const rows = data?.rows ?? [];
  const totalCount = data?.total ?? 0;

  return {
    statementMonth,
    handleMonthChange,
    currentPage,
    totalPages,
    handlePageChange,
    visibleColumns,
    setVisibleColumns,
    columnFilterItems,
    visibleLabels,
    rows,
    summary,
    breakdowns,
    totalCount,
    isLoading,
    isFetching,
    isError,
    error,
  };
};
